import React, { useEffect, useState } from "react";
import logo from "../assets/logo/logo-1.png";
import { useNavigate, useLocation } from "react-router-dom";
import OAuth from "../components/Form/OAuth";
import useNotification from "../components/notifications/useNotification";
import { useLoginMutation, useSignUpMutation } from "../store/apis/auth.api";
import { useVerify2FAMutation, useResend2FACodeMutation } from "../store/apis/twoFactor.api";
import {
  MdLock,
  MdOutlineAlternateEmail,
  MdSupervisedUserCircle,
} from "react-icons/md";
import { getStorage } from "../utils/localStorage";
import useNavigateTo from "../hooks/useNavigateTo";
import { useDispatch } from "react-redux";
import { signInSuccess } from "../store/slices/user.slice";
import { FaUser, FaPhone } from "react-icons/fa";
import PasswordInput from "../components/Form/PasswordInput";
import EmailInput from "../components/Form/EmailInput";
import { passwordMeetsPolicy } from "../utils/passwordPolicy";
import { track } from "../utils/analytics";

// Logo is 905x223. Stacking animation uses pixel-perfect bar overlays.
// Bar positions at 260px wide (h=64px): top y=5 h=15, middle y=25 h=15, bottom y=44 h=14
// Bar positions at 128px wide (h=32px): top y=2 h=7, middle y=12 h=7, bottom y=22 h=7

function StackedLogo({ width, height, barPositions, onClick, extraClass = "" }) {
  const [hovered, setHovered] = useState(false);
  const iconWidth = Math.round(width * 0.22);

  return (
    <>
      <style>{`
        .icon-hider {
          position: absolute;
          left: 0; top: 0;
          background-color: transparent;
          pointer-events: none;
          opacity: 0;
        }
        .icon-hider.on { opacity: 1; }

        .bar-tile {
          position: absolute;
          left: 0;
          pointer-events: none;
          opacity: 0;
          transform: translateY(-50px);
        }
        @keyframes dropIn {
          0%   { opacity: 0;   transform: translateY(-40px); }
          30%  { opacity: 1;   transform: translateY(4px); }
          55%  { transform: translateY(-2px); }
          75%  { transform: translateY(1px); }
          100% { opacity: 1;   transform: translateY(0px); }
        }
        .bar-tile.drop { animation: dropIn 0.7s cubic-bezier(0.25, 0.8, 0.35, 1) forwards; }
        .bar-tile.drop.d0 { animation-delay: 0.15s; }
        .bar-tile.drop.d1 { animation-delay: 1.05s; }
        .bar-tile.drop.d2 { animation-delay: 1.95s; }
        .bar-tile.reset { opacity: 1; transform: translateY(0); animation: none; }
      `}</style>
      <div
        className={`relative cursor-pointer ${extraClass}`}
        style={{ width: `${width}px`, height: `${height}px` }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={onClick}
      >
        <img
          src={logo}
          alt="logo"
          style={{ width: `${width}px`, height: `${height}px`, display: "block" }}
          loading="eager"
        />

        {/* Solid cover hides bars instantly on hover */}
        <div
          className={`icon-hider${hovered ? " on" : ""}`}
          style={{ width: `${iconWidth}px`, height: `${height}px`, backgroundColor: "#0f1421" }}
        />

        {/* Bottom bar — drops first */}
        <div
          className={`bar-tile${hovered ? " drop d0" : " reset"}`}
          style={{
            top: `${barPositions.bottom.top}px`,
            height: `${barPositions.bottom.h}px`,
            width: `${iconWidth}px`,
            backgroundImage: `url(${logo})`,
            backgroundSize: `${width}px ${height}px`,
            backgroundPosition: `0px -${barPositions.bottom.top}px`,
            backgroundRepeat: "no-repeat",
          }}
        />
        {/* Middle bar — drops second */}
        <div
          className={`bar-tile${hovered ? " drop d1" : " reset"}`}
          style={{
            top: `${barPositions.middle.top}px`,
            height: `${barPositions.middle.h}px`,
            width: `${iconWidth}px`,
            backgroundImage: `url(${logo})`,
            backgroundSize: `${width}px ${height}px`,
            backgroundPosition: `0px -${barPositions.middle.top}px`,
            backgroundRepeat: "no-repeat",
          }}
        />
        {/* Top bar — drops last */}
        <div
          className={`bar-tile${hovered ? " drop d2" : " reset"}`}
          style={{
            top: `${barPositions.top.top}px`,
            height: `${barPositions.top.h}px`,
            width: `${iconWidth}px`,
            backgroundImage: `url(${logo})`,
            backgroundSize: `${width}px ${height}px`,
            backgroundPosition: `0px -${barPositions.top.top}px`,
            backgroundRepeat: "no-repeat",
          }}
        />
      </div>
    </>
  );
}

const Login = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  // Default mode: signup if the user hit /signup directly, otherwise
  // honor ?mode=signup or fall through to login. The /signup route
  // alias was added in App.jsx so marketing CTAs can deep-link
  // straight into the signup form.
  const pathIsSignup = location.pathname === "/signup";
  const paramMode = searchParams.get("mode") || (pathIsSignup ? "signup" : "login");
  const [mode, setMode] = useState(paramMode);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Collect first + last name in separate fields. A single "Full Name"
  // box is a data-quality trap — people drop a company, a nickname, or
  // just a first name in there, and we lose the structure we need for
  // personalized outreach. Phone is captured here too so the team can
  // follow up with signups. All three flow to the account + admin board.
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Combined name kept for everything downstream that reads `user.name`
  // (greetings, admin list, Sentry). Backend also gets firstName/lastName
  // separately so the admin board can show structured columns.
  const fullName = `${firstName} ${lastName}`.trim();
  // Phone is "valid enough" when it has at least 10 digits — lenient so
  // international formats and punctuation styles all pass.
  const phoneDigits = phone.replace(/\D/g, "");
  const phoneValid = phoneDigits.length >= 10;

  // Duplicate-email conflict on signup. When the backend rejects a
  // signup because the email is already attached to an account, we
  // surface an inline banner (not a toast) with two clear actions:
  // sign in to that account, or use a different email for a fresh
  // demo. Enforces the "one account per email, demo OR paid not
  // both" rule without needing a separate API call. Stores the
  // conflicting address so the swap-to-login flow can prefill it.
  const [emailConflict, setEmailConflict] = useState(null);

  const [login] = useLoginMutation();
  const [verify2FA] = useVerify2FAMutation();
  const [resend2FACode] = useResend2FACodeMutation();

  // 2FA challenge state — only populated after backend returns requires2FA:true
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFactorUserId, setTwoFactorUserId] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [verifying2FA, setVerifying2FA] = useState(false);
  const [twoFactorMethod, setTwoFactorMethod] = useState("email");  // "email" | "totp"
  const [resendCooldown, setResendCooldown] = useState(0);            // seconds until resend allowed
  const [signUp] = useSignUpMutation();

  const loginUser = getStorage("__user__", "object");
  const navigate = useNavigate();
  const { getNavigation } = useNavigateTo();
  const notify = useNotification();

  const handleLogoClick = () => navigate("/");

  const handleAuth = async () => {
    try {
      if (mode === "login") {
        const response = await login({ email, password });
        if (response?.data?.success) {
          const data = response?.data?.data;

          // 2FA branch — backend says password is OK but needs the code.
          const pendingId = data?.pendingUserId || data?.userId;
          if (data?.requires2FA && pendingId) {
            setRequires2FA(true);
            setTwoFactorUserId(pendingId);
            const method = data?.method === "totp" ? "totp" : "email";
            setTwoFactorMethod(method);
            if (method === "email") setResendCooldown(30);
            try { sessionStorage.setItem("__2fa_pending_user__", pendingId); } catch { /* ignore */ }
            notify({
              message: method === "totp"
                ? "Enter the 6-digit code from your authenticator app"
                : data?.codeSent
                  ? "We just emailed you a 6-digit code"
                  : "Enter your 6-digit code",
              type: "info",
            });
            return;
          }

          if (!data?.token) {
            throw new Error("Login response missing token. Please try again.");
          }
          dispatch(signInSuccess(data));
          notify({ message: "Login successful!", type: "success" });
          // If the user was bounced here from a demo gate (or any
          // other gated route), send them back to that path instead
          // of the generic role-based landing page. Demo paths are
          // the primary use case — a returning visitor signing in
          // expects to land on the demo they were trying to view.
          const fromPath = location.state?.from?.pathname;
          if (fromPath && fromPath.startsWith("/demo")) {
            navigate(fromPath, { replace: true });
            return;
          }
          getNavigation(data);
        } else throw new Error(response?.error?.data?.message);
      } else {
        // Tag the signup with where it came from so the admin
        // follow-up view can segment "demo gate, didn't convert"
        // from "organic, didn't pick a plan." When state.from is
        // a /demo path the visitor was bounced here by the demo
        // gate; otherwise it's a direct signup.
        const fromPath = location.state?.from?.pathname;
        const signupSource = fromPath && fromPath.startsWith("/demo")
          ? "demo_gate"
          : "organic";
        const signUpResponse = await signUp({
          // `name` (combined) keeps every existing consumer working;
          // firstName/lastName/phone are the structured fields the admin
          // board + outreach need. Backend must persist firstName/lastName
          // (phone already exists on the user model).
          name: fullName,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          phone: phone.trim(),
          email, password, confirmPassword,
          agreedToTerms: true,
          signupSource,
        });
        if (signUpResponse?.data?.success) {
          // Funnel step 2 of landing → signup → demo → convert → paid. This
          // was the one stage firing nothing, so the drop-off between landing
          // and demo start was invisible. signupSource carries where they came
          // from; no email or name goes to the provider (analytics.js strips
          // identifiers before anything leaves the app).
          track("signup_completed", { signupSource: signupSource || "direct" });
          // Auto-login chain — backend's signup returns the User record
          // but no token, so we immediately POST /auth/login with the
          // same credentials and dispatch signInSuccess. Without this
          // the user sees "Signup successful! Now log in" and has to
          // type their email + password a second time, which is a real
          // bounce point on the signup flow ("wait, did it not work?").
          // The login call goes through the same 2FA branch as a
          // normal login, but a brand-new account never has 2FA
          // enabled so we land in the data?.token path immediately.
          const loginResponse = await login({ email, password });
          if (loginResponse?.data?.success) {
            const data = loginResponse?.data?.data;
            if (data?.token) {
              dispatch(signInSuccess(data));
              // If the signup was triggered by the demo gate (state.from
              // is a /demo path), send them straight to the demo they
              // were trying to view. Otherwise default to /pricing —
              // a brand-new (planless, status:disabled) user needs to
              // finish checkout, and ProtectedRoute would bounce them
              // there anyway via its isBlocked check.
              const fromPath = location.state?.from?.pathname;
              if (fromPath && fromPath.startsWith("/demo")) {
                notify({ message: "Welcome to Stacked! Starting your demo…", type: "success" });
                navigate(fromPath, { replace: true });
                return;
              }
              notify({ message: "Welcome to Stacked! Let's pick your plan.", type: "success" });
              navigate("/pricing", { replace: true });
              return;
            }
          }
          // Auto-login fell through (rare — backend hiccup). Fall back
          // to the old "go log in" behavior so the user isn't stuck.
          notify({ message: "Account created. Please log in.", type: "success" });
          setMode("login");
          setFirstName("");
          setLastName("");
          setPhone("");
        } else throw new Error(signUpResponse?.error?.data?.message);
      }
    } catch (error) {
      const msg = error?.message || "Something went wrong";
      // Duplicate-email signup → surface as an inline banner with
      // route-out actions instead of a toast. Match the backend's
      // existing copy ("already exists", "in use", "already
      // registered") without coupling to one exact string — keeps
      // this resilient if the wording shifts. Only intercept in
      // signup mode; the "no such user" branch on login should stay
      // a toast.
      if (mode === "signup" && /already (exists|in use|registered)|email.*(in use|taken)/i.test(msg)) {
        setEmailConflict({ email, message: msg });
        return;
      }
      notify({ message: msg, type: "error" });
    }
  };

  const handleVerify2FA = async () => {
    if (!twoFactorCode || twoFactorCode.length < 6) {
      notify({ message: "Enter the 6-digit code from your authenticator app", type: "error" });
      return;
    }
    // Recover userId from sessionStorage if React state was dropped.
    let effectiveUserId = twoFactorUserId;
    if (!effectiveUserId) {
      try { effectiveUserId = sessionStorage.getItem("__2fa_pending_user__"); } catch { /* ignore */ }
    }
    if (!effectiveUserId) {
      notify({ message: "Session expired. Please enter your password again.", type: "error" });
      setRequires2FA(false);
      return;
    }
    setVerifying2FA(true);
    try {
      const response = await verify2FA({
        // Must be `pendingUserId` — global stripServerManagedFields middleware
        // would delete a field named `userId` before this request reaches the
        // verify controller.
        pendingUserId: effectiveUserId,
        code: twoFactorCode,
        isBackupCode: useBackupCode,
      });
      if (response?.data?.success) {
        const data = response.data.data;
        if (!data?.token) throw new Error("2FA verification did not return a token.");
        try { sessionStorage.removeItem("__2fa_pending_user__"); } catch { /* ignore */ }
        dispatch(signInSuccess(data));
        notify({ message: "Login successful!", type: "success" });
        getNavigation(data);
      } else {
        throw new Error(response?.error?.data?.message || "Invalid 2FA code");
      }
    } catch (error) {
      notify({ message: error?.message || "Invalid 2FA code", type: "error" });
    } finally {
      setVerifying2FA(false);
    }
  };

  // Tick down the resend-cooldown countdown each second.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(s => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  useEffect(() => {
    const alreadyRedirected = sessionStorage.getItem("__login_redirected__");
    // If we were bounced here from a gate elsewhere in the app (e.g.
    // AddonGate, ProtectedRoute, AuthContext-null bounce), location.state
    // carries the original target. In that case the user is already
    // authenticated and just hit a gate they shouldn't have — sending
    // them to a generic role-based landing page (which for admin is
    // /admin/dashboard) overrides their actual intent. Skip the
    // auto-redirect; the gate that bounced them will re-evaluate after
    // they re-authenticate.
    const cameFromBounce = !!location.state?.from;
    // Require a real token, not just the __user__ blob. ProtectedRoute
    // gates on __login_user_token__; if we auto-redirect on the blob
    // alone, a leftover __user__ with no/invalid token bounces to the
    // dashboard, ProtectedRoute bounces back here, and we loop. Gating
    // on the token keeps Login and ProtectedRoute in agreement.
    const loginToken = getStorage("__login_user_token__");
    if (loginToken && loginUser && loginUser.role && !alreadyRedirected && !cameFromBounce) {
      sessionStorage.setItem("__login_redirected__", "true");
      getNavigation(loginUser);
    }
    return () => sessionStorage.removeItem("__login_redirected__");
  }, []);

  // Bar positions scaled to each logo size
  const lgBarPositions = {
    bottom: { top: 44, h: 14 },
    middle: { top: 25, h: 15 },
    top:    { top: 5,  h: 15 },
  };
  const smBarPositions = {
    bottom: { top: 22, h: 7 },
    middle: { top: 12, h: 7 },
    top:    { top: 2,  h: 7 },
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0f1421]">
      <div className="fixed inset-0 bg-[#0f1421] w-full h-full overflow-hidden"></div>

      {/* 2FA challenge modal — shown when backend says requires2FA */}
      {requires2FA && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-[#1a1f2e] rounded-xl border border-[#1ec8c8]/40 shadow-2xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-white mb-2">Two-Factor Authentication</h2>
            <p className="text-gray-400 text-sm mb-5">
              {useBackupCode
                ? "Enter one of your backup codes."
                : twoFactorMethod === "totp"
                  ? "Enter the 6-digit code from your authenticator app."
                  : "We just emailed you a 6-digit code. It expires in 10 minutes."}
            </p>
            <input
              type="text"
              inputMode={useBackupCode ? "text" : "numeric"}
              autoComplete="one-time-code"
              autoFocus
              value={twoFactorCode}
              maxLength={useBackupCode ? 16 : 6}
              onChange={(e) =>
                setTwoFactorCode(
                  useBackupCode
                    ? e.target.value.trim()
                    : e.target.value.replace(/\D/g, "").slice(0, 6)
                )
              }
              onKeyDown={(e) => {
                // Guard against double-submit: the button below already
                // disables on `verifying2FA`, but the Enter handler had no
                // such guard, so a fast double-Enter could fire two
                // verify requests. The second one would fail with "code
                // already used" and confuse the user.
                if (e.key === "Enter" && !verifying2FA) handleVerify2FA();
              }}
              className="block w-full text-center text-2xl tracking-widest font-mono rounded-md py-3 px-4 bg-[#324250] border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-[#1ec8c8]"
              placeholder={useBackupCode ? "abcdef12" : "123456"}
            />
            <button
              onClick={handleVerify2FA}
              disabled={verifying2FA}
              className="mt-4 w-full py-3 rounded-md bg-[#1ec8c8] hover:bg-[#34b8b3] text-black font-semibold disabled:opacity-50"
            >
              {verifying2FA ? "Verifying…" : "Verify and Sign In"}
            </button>
            {twoFactorMethod === "email" && !useBackupCode && (
              <button
                onClick={async () => {
                  if (resendCooldown > 0) return;
                  const pid = twoFactorUserId || (() => {
                    try { return sessionStorage.getItem("__2fa_pending_user__"); } catch { return null; }
                  })();
                  if (!pid) return;
                  try {
                    const res = await resend2FACode({ pendingUserId: pid });
                    if (res?.data?.success) {
                      notify({ message: "New code sent. Check your email.", type: "success" });
                      setResendCooldown(30);
                    } else {
                      notify({ message: res?.error?.data?.message || "Could not resend code", type: "error" });
                    }
                  } catch (err) {
                    notify({ message: err?.message || "Could not resend code", type: "error" });
                  }
                }}
                disabled={resendCooldown > 0}
                type="button"
                className="mt-2 w-full text-xs text-gray-400 hover:text-[#1ec8c8] underline disabled:opacity-50 disabled:no-underline"
              >
                {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : "Didn't get the email? Resend code"}
              </button>
            )}
            <div className="flex justify-between text-xs text-gray-400 mt-3">
              <button
                onClick={() => { setUseBackupCode(!useBackupCode); setTwoFactorCode(""); }}
                className="hover:text-[#1ec8c8] underline"
                type="button"
              >
                {useBackupCode
                  ? (twoFactorMethod === "totp" ? "Use authenticator app" : "Use email code")
                  : "Use a backup code"}
              </button>
              <button
                onClick={() => { setRequires2FA(false); setTwoFactorCode(""); setTwoFactorUserId(null); }}
                className="hover:text-[#1ec8c8] underline"
                type="button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile logo — top left */}
      <div className="fixed top-2 left-4 sm:left-8 z-20 lg:hidden">
        <StackedLogo
          width={128}
          height={32}
          barPositions={smBarPositions}
          onClick={handleLogoClick}
        />
      </div>

      <div className="relative w-full sm:flex sm:flex-row gap-6 justify-center items-center">
        {/* Left panel — desktop only */}
        <div className="flex-col flex self-center lg:px-14 sm:max-w-4xl xl:max-w-md z-10">
          <div className="self-start hidden lg:flex flex-col text-white gap-5">
            <StackedLogo
              width={260}
              height={64}
              barPositions={lgBarPositions}
              onClick={handleLogoClick}
            />
            <h1 className="font-semibold text-5xl">
              {/* Context-aware heading. If the visitor was bounced
                  here from a /demo gate, lead with the demo CTA
                  instead of the generic signup pitch — the page they
                  were actually trying to reach is a tour. */}
              {(() => {
                const fromDemo = location.state?.from?.pathname?.startsWith("/demo");
                if (mode === "login") return "Welcome back.";
                if (fromDemo) return "Sign up to start your demo.";
                return "Create your account.";
              })()}
            </h1>
            <p className="pr-3 text-sm opacity-75 leading-relaxed">
              {(() => {
                const fromDemo = location.state?.from?.pathname?.startsWith("/demo");
                if (mode === "login") {
                  return "Sign in to your account and get back to building your business.";
                }
                if (fromDemo) {
                  return "Create an account to launch your 24-hour demo — no credit card needed. After the tour you can pick a plan and turn it into a real account.";
                }
                return "New to Stacked? Create your account below. Once you're in, you'll choose a plan that fits your business and get set up in minutes.";
              })()}
            </p>
          </div>
        </div>

        {/* Right panel — form */}
        <div className="flex justify-center self-center z-10 px-4 py-6 sm:px-0">
          <div className="p-8 sm:p-12 bg-[var(--lighter-dark)] bg-opacity-95 mx-auto rounded-3xl w-full max-w-md shadow-xl">
            <div className="mb-6">
              <h3 className="font-semibold text-xl tracking-wider text-[var(--gray)]">
                {mode === "login" ? "Login" : "Sign Up"}
              </h3>
            </div>

            {/* Duplicate-email guard. Renders when the backend
                rejects a signup because the email is already on an
                account. Enforces "one email = one account (demo OR
                paid, not both)" by routing the visitor to either
                sign in or use a different address. */}
            {emailConflict && (
              <div className="mb-5 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4">
                <p className="text-amber-200 text-sm font-semibold mb-1">
                  This email already has an account.
                </p>
                <p className="text-amber-100/80 text-xs leading-relaxed mb-3">
                  Subscriptions and demos can't share the same email — sign in to your existing account, or use a different email to start a fresh demo.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      // Swap to login mode and keep the email
                      // prefilled so the visitor only needs to type
                      // their password to recover.
                      setMode("login");
                      setPassword("");
                      setEmailConflict(null);
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1ec8c8] hover:bg-[#34b8b3] text-black text-xs font-semibold transition-colors"
                  >
                    Sign in to {emailConflict.email}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      // Clear the email field so the cursor lands
                      // ready to type a new address.
                      setEmail("");
                      setEmailConflict(null);
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-amber-100 text-xs font-semibold transition-colors"
                  >
                    Use a different email
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {mode === "signup" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-300 mb-2">
                      First Name
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaUser size={14} className="text-gray-400" />
                      </div>
                      <input
                        id="firstName"
                        name="firstName"
                        type="text"
                        autoComplete="given-name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="block w-full rounded-lg pl-10 py-2.5 pr-3 bg-[#324250] border border-white/10 focus:border-[#1ec8c8]/60 focus:ring-2 focus:ring-[#1ec8c8]/30 placeholder-gray-500 text-white focus:outline-none transition-colors"
                        placeholder="First"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-300 mb-2">
                      Last Name
                    </label>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      autoComplete="family-name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="block w-full rounded-lg px-3 py-2.5 bg-[#324250] border border-white/10 focus:border-[#1ec8c8]/60 focus:ring-2 focus:ring-[#1ec8c8]/30 placeholder-gray-500 text-white focus:outline-none transition-colors"
                      placeholder="Last"
                    />
                  </div>
                </div>
              )}

              <EmailInput
                name="email"
                value={email}
                onChange={(value) => {
                  setEmail(value);
                  // Editing the address invalidates the conflict
                  // banner — clear it so the visitor doesn't see a
                  // stale "this email already has an account" warning
                  // tied to the address they just changed.
                  if (emailConflict) setEmailConflict(null);
                }}
                label="Email Address"
                id="email"
                // Mark this as the login identifier so password managers
                // map the saved credential's username to the EMAIL field.
                // Without "username" the browser guesses, and on the signup
                // form (which also has a tel field) it had been saving the
                // phone number as the username — then autofilling that phone
                // into the email box on the next sign-in.
                autoComplete="username"
              />

              {mode === "signup" && (
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                    Phone Number
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaPhone size={13} className="text-gray-400" />
                    </div>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      autoComplete="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="block w-full rounded-lg pl-10 py-2.5 pr-4 bg-[#324250] border border-white/10 focus:border-[#1ec8c8]/60 focus:ring-2 focus:ring-[#1ec8c8]/30 placeholder-gray-500 text-white focus:outline-none transition-colors"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
              )}

              <PasswordInput
                name="password"
                value={password}
                onChange={(value) => setPassword(value)}
                label="Password"
                id="Password"
                showStrength={mode === "signup"}
                rulesContext={mode === "signup" ? { email, name: fullName } : undefined}
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
              />

              {mode === "signup" && (
                <PasswordInput
                  name="confirmPassword"
                  value={confirmPassword}
                  onChange={(value) => setConfirmPassword(value)}
                  label="Confirm Password"
                  id="confirmPassword"
                  autoComplete="new-password"
                />
              )}

              <div>
                {(() => {
                  // In signup mode, block submit until the required fields
                  // are filled (first + last name, a plausible phone),
                  // the password meets policy, AND confirm matches. In
                  // login mode, no such gate — old passwords still accepted.
                  const signupBlocked =
                    mode === "signup" &&
                    (!firstName.trim() ||
                     !lastName.trim() ||
                     !phoneValid ||
                     !passwordMeetsPolicy(password, { email, name: fullName }) ||
                     password !== confirmPassword);
                  return (
                    <button
                      onClick={handleAuth}
                      disabled={signupBlocked}
                      className={
                        "w-full flex justify-center py-2.5 px-4 rounded-lg shadow-sm text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#1ec8c8]/40 transition-all " +
                        (signupBlocked
                          ? "bg-[#1ec8c8]/30 text-black/60 cursor-not-allowed"
                          : "bg-[#1ec8c8] hover:bg-[#34b8b3] text-black active:translate-y-px")
                      }
                    >
                      {mode === "login" ? "Login" : "Sign Up"}
                    </button>
                  );
                })()}
              </div>

              {mode === "signup" && (
                <p className="text-xs text-gray-400 text-center leading-relaxed mt-2">
                  By creating an account, you agree to our{" "}
                  <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="text-[#1ec8c8] hover:underline">Terms of Service</a>
                  {" "}and{" "}
                  <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#1ec8c8] hover:underline">Privacy Policy</a>.
                </p>
              )}

              <div className="flex items-center justify-between">
                <div className="text-sm">
                  <button
                    onClick={() => {
                      const newMode = mode === "login" ? "signup" : "login";
                      setMode(newMode);
                      navigate(`/login?mode=${newMode}`);
                    }}
                    className="text-[var(--gray)] cursor-pointer focus:outline-none"
                  >
                    {mode === "login" ? "New user? Sign up" : "Already have an account? Login"}
                  </button>
                </div>
                {mode === "login" && (
                  <div className="text-sm ml-auto">
                    <button
                      type="button"
                      onClick={() => navigate("/forgot-password")}
                      className="text-[var(--gray)] cursor-pointer hover:text-teal-400 focus:outline-none"
                    >
                      Forgot password?
                    </button>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-center space-x-2 my-5">
                <span className="h-px w-16 bg-gray-300"></span>
                <span className="text-gray-400 font-normal">or continue with</span>
                <span className="h-px w-16 bg-gray-300"></span>
              </div>

              <OAuth />
            </div>

            <div className="mt-7 text-center text-gray-400 text-xs">
              <span>Copyright {new Date().getFullYear()} Stacked. All rights reserved.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
